import React from 'react';
import {
  ItineraryBadge,
  ItineraryBadgeBadgeTypeEnum,
  ItineraryBadgeIconEnum,
} from '@kiwicom/openapi-sdk/mmb';
import Tooltip from '@kiwicom/orbit-components/lib/Tooltip';
import Accommodation from '@kiwicom/orbit-components/lib/icons/Accommodation';
import AlertCircle from '@kiwicom/orbit-components/lib/icons/AlertCircle';
import BaggageCheckedNone from '@kiwicom/orbit-components/lib/icons/BaggageCheckedNone';
import BaggageSet from '@kiwicom/orbit-components/lib/icons/BaggageSet';
import Clock from '@kiwicom/orbit-components/lib/icons/Clock';
import Diamond from '@kiwicom/orbit-components/lib/icons/Diamond';
import InformationCircle from '@kiwicom/orbit-components/lib/icons/InformationCircle';
import Guarantee from '@kiwicom/orbit-components/lib/icons/Guarantee';
import Moon from '@kiwicom/orbit-components/lib/icons/Moon';
import SelfTransfer from '@kiwicom/orbit-components/lib/icons/SelfTransfer';
import StarFull from '@kiwicom/orbit-components/lib/icons/StarFull';
import Visa from '@kiwicom/orbit-components/lib/icons/Visa';
import {
  ItineraryBadgeList as OrbitItineraryBadgeList,
  ItineraryBadgeListItem as OrbitItineraryBadgeListItem,
} from '@kiwicom/orbit-components/lib/Itinerary';
import { Type as OrbitBadgeType } from '@kiwicom/orbit-components/lib/BadgeList/BadgeListItem/types';
import Text from '@kiwicom/orbit-components/lib/Text';
import { Type as OrbitTextType } from '@kiwicom/orbit-components/lib/Text/types.d';
import List, { ListItem } from '@kiwicom/orbit-components/lib/List';
import { useLog } from '@kiwicom/nitro/lib/services/log/context';
import { Event as EventLog, Props as EventProps } from '@kiwicom/nitro/lib/records/Event';
import { Category, createEventLogUnified, ErrorAction } from '@/common/services/logging';
import { SpaceAfterSizes } from '@kiwicom/orbit-components/lib/common/types.d';
import { Spacing } from '@kiwicom/orbit-components/lib/Stack/types.d';
import Check from '@kiwicom/orbit-components/lib/icons/Check';
import useLabelClose from '../../../services/hooks/useLabelClose';

// For some reason, this can't be a component because than Orbit won't apply correct styles on the badge icon.
// We can't call useLog hook, so we are passing log function as a prop.
function getBadgeIcon(
  icon: ItineraryBadgeIconEnum,
  log: (ev: EventLog, props: EventProps) => Promise<void>,
) {
  switch (icon) {
    case ItineraryBadgeIconEnum.Accommodation:
      return <Accommodation ariaHidden />;
    case ItineraryBadgeIconEnum.Alert:
      return <AlertCircle ariaHidden />;
    case ItineraryBadgeIconEnum.Baggage:
      return <BaggageSet ariaHidden />;
    case ItineraryBadgeIconEnum.Clock:
      return <Clock ariaHidden />;
    case ItineraryBadgeIconEnum.Check:
      return <Check ariaHidden />;
    case ItineraryBadgeIconEnum.Diamond:
      return <Diamond ariaHidden />;
    case ItineraryBadgeIconEnum.Guarantee:
      return <Guarantee ariaHidden />;
    case ItineraryBadgeIconEnum.Info:
      return <InformationCircle ariaHidden />;
    case ItineraryBadgeIconEnum.Moon:
      return <Moon ariaHidden />;
    case ItineraryBadgeIconEnum.NoBaggage:
      return <BaggageCheckedNone ariaHidden />;
    case ItineraryBadgeIconEnum.SelfTransfer:
      return <SelfTransfer ariaHidden />;
    case ItineraryBadgeIconEnum.Star:
      return <StarFull ariaHidden />;
    case ItineraryBadgeIconEnum.Visa:
      return <Visa ariaHidden />;
    default: {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const exhaustivenessCheck: never = icon;

      void log(
        createEventLogUnified(Category.COMMON, ErrorAction.InvalidData, 'itinerary badge icon'),
        {
          receivedValue: icon,
        },
      );

      return null;
    }
  }
}

function getOrbitBadgeType(badgeType: ItineraryBadgeBadgeTypeEnum): OrbitBadgeType {
  return badgeType === ItineraryBadgeBadgeTypeEnum.Default ? 'neutral' : badgeType;
}

function getOrbitTextType(badgeType: ItineraryBadgeBadgeTypeEnum): OrbitTextType {
  return badgeType === ItineraryBadgeBadgeTypeEnum.Default ||
    badgeType === ItineraryBadgeBadgeTypeEnum.Neutral
    ? 'primary'
    : badgeType;
}

// Ideally, we should use Orbit's TextLink component with the appropriate type prop
// to create links with proper styling. However, here we're getting the html from
// the server, and it contains plain `<a>` tags. Thus, we need to add the styling
// ourselves (unless we want to parse the html, which we don't).
const classNameLinkStyle = '[&>a]:underline hover:[&>a]:no-underline [&>a]:font-medium';

const ItineraryBadgeListItemContentRefTag = ({
  badgeValue,
  badgeType,
}: {
  badgeValue: string;
  badgeType: ItineraryBadgeBadgeTypeEnum;
}) => {
  if (badgeValue.includes('<ref>')) {
    const [beforeRef, refText, afterRef] = badgeValue.split(/<\/*ref>/);
    return (
      <>
        {beforeRef && (
          // eslint-disable-next-line react/no-danger,@typescript-eslint/naming-convention
          <span dangerouslySetInnerHTML={{ __html: beforeRef }} className={classNameLinkStyle} />
        )}
        <Text type={getOrbitTextType(badgeType)} size="small" as="span" weight="medium">
          {refText}
        </Text>
        {afterRef && (
          // eslint-disable-next-line react/no-danger,@typescript-eslint/naming-convention
          <span dangerouslySetInnerHTML={{ __html: afterRef }} className={classNameLinkStyle} />
        )}
      </>
    );
  }
  // eslint-disable-next-line react/no-danger,@typescript-eslint/naming-convention
  return <span dangerouslySetInnerHTML={{ __html: badgeValue }} className={classNameLinkStyle} />;
};

/**
 * Component which will correctly render content of badge from server-driven Itinerary.
 * It supports rendering traditional HTML tags, but also pseudo-HTML tags such as <tooltip> and <ref>.
 * Part of the text which should be rendered as tooltip is wrapped in <tooltip> tag.
 * Part of the text which should be rendered with the same color as the badge is wrapped in <ref> tag.
 *
 * @param badgeValue - Text of the badge object which is part of server-driven Itinerary
 * @param badgeTooltip - List of texts in tooltip of the badge object.
 * @param badgeType - Type (color) of the badge object.
 *
 * @returns BadgeListItem component
 */
const ItineraryBadgeListItemContent = ({
  badgeValue,
  badgeTooltip,
  badgeType,
}: {
  badgeValue: string;
  badgeTooltip?: string[];
  badgeType: ItineraryBadgeBadgeTypeEnum;
}) => {
  const labelClose = useLabelClose();
  if (badgeValue.includes('<tooltip>')) {
    // There should be only one tooltip in the badgeValue according to the API agreement.
    // If there will be additional tooltips we don't have the content for them in the
    // response either way, so we can't render it.
    const [beforeTooltip, tooltipText, afterTooltip] = badgeValue.split(/<\/*tooltip>/);
    return (
      <>
        {beforeTooltip && (
          <ItineraryBadgeListItemContentRefTag badgeValue={beforeTooltip} badgeType={badgeType} />
        )}
        <Tooltip
          content={
            <List>
              {badgeTooltip?.map(tooltipText => (
                <ListItem key={tooltipText}>
                  <span
                    className="[&>a]:underline"
                    // eslint-disable-next-line react/no-danger,@typescript-eslint/naming-convention
                    dangerouslySetInnerHTML={{ __html: tooltipText }}
                  />
                </ListItem>
              ))}
            </List>
          }
          labelClose={labelClose}
        >
          <Text as="span">{tooltipText}</Text>
        </Tooltip>
        {afterTooltip && (
          <ItineraryBadgeListItemContentRefTag badgeValue={afterTooltip} badgeType={badgeType} />
        )}
      </>
    );
  }
  return <ItineraryBadgeListItemContentRefTag badgeValue={badgeValue} badgeType={badgeType} />;
};

type ItineraryBadgeListProps = {
  badges: ItineraryBadge[];
  spaceAfter?: SpaceAfterSizes;
  spacing?: Spacing;
};

/**
 * Component for rendering ItineraryBadgeList object which comes as part of server-driven Itinerary schema.
 * It is used at the end of sectors, segment groups and segments. Content of the badges supports HTML tags and tooltips.
 *
 * @param badges - Array of {@link ItineraryBadge} objects coming from backend
 * @param spaceAfter - Space after value which is passed to underlying Orbit's {@link OrbitItineraryBadgeList} component. Default is 'normal'.
 * @param spacing - Spacing value which is applied to spacing between badges. Default is '100' (4px).
 *
 * @returns ItineraryBadgeList component
 */
const ItineraryBadgeList = ({
  badges,
  spaceAfter = 'normal',
  spacing = '100',
}: ItineraryBadgeListProps) => {
  const { log } = useLog();
  return (
    <OrbitItineraryBadgeList
      spaceAfter={spaceAfter}
      dataTest="ItineraryBadgeList"
      spacing={spacing}
    >
      {badges.map(badge => (
        <OrbitItineraryBadgeListItem
          key={badge.value}
          icon={getBadgeIcon(badge.icon, log)}
          type={getOrbitBadgeType(badge.badgeType)}
          cancelledValue={badge.originalValues?.value}
          withBackground={Boolean(badge.originalValues) || badge.isValueHighlighted}
        >
          <ItineraryBadgeListItemContent
            badgeValue={badge.value}
            badgeTooltip={badge.tooltip}
            badgeType={badge.badgeType}
          />
        </OrbitItineraryBadgeListItem>
      ))}
    </OrbitItineraryBadgeList>
  );
};

export default ItineraryBadgeList;
